import { SYSTEM_RACE_CLOCK, type RaceClock } from '../shared/race-clock.js';

export type RaceDiagnosticCategory =
  | 'runtime'
  | 'transport'
  | 'simulation'
  | 'presentation'
  | 'persistence';

export interface RaceDiagnosticEntry {
  readonly sequence: number;
  readonly wallAt: number;
  readonly monotonicAt: number;
  readonly category: RaceDiagnosticCategory;
  readonly event: string;
  readonly details?: unknown;
}

export interface RaceDiagnosticExport {
  readonly version: 1;
  readonly generatedAt: number;
  readonly entries: readonly RaceDiagnosticEntry[];
}

const SECRET_KEY = /(token|secret|credential|authorization|cookie|password)/i;
const MAX_STRING_LENGTH = 240;
const MAX_ARRAY_LENGTH = 24;
const MAX_OBJECT_KEYS = 32;
const MAX_DEPTH = 4;

/** Bounded, serialisable, credential-redacting runtime journal. */
export class RaceDiagnostics {
  private readonly entries: RaceDiagnosticEntry[] = [];
  private sequence = 0;

  constructor(
    private readonly clock: RaceClock = SYSTEM_RACE_CLOCK,
    private readonly limit = 256,
  ) {}

  record(category: RaceDiagnosticCategory, event: string, details?: unknown): void {
    const safeEvent = event.trim().slice(0, 80) || 'unknown';
    this.entries.push({
      sequence: ++this.sequence,
      wallAt: this.clock.wallNow(),
      monotonicAt: this.clock.monotonicNow(),
      category,
      event: safeEvent,
      ...(details === undefined ? {} : { details: sanitiseDiagnosticValue(details) }),
    });
    while (this.entries.length > Math.max(1, this.limit)) this.entries.shift();
  }

  export(): RaceDiagnosticExport {
    return structuredClone({
      version: 1,
      generatedAt: this.clock.wallNow(),
      entries: this.entries,
    });
  }

  clear(): void {
    this.entries.length = 0;
  }
}

export function sanitiseDiagnosticValue(value: unknown, depth = 0): unknown {
  if (depth >= MAX_DEPTH) return '[truncated]';
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : String(value);
  if (typeof value === 'string') return value.slice(0, MAX_STRING_LENGTH);
  if (typeof value === 'bigint') return value.toString();
  if (value instanceof Error) {
    return {
      name: value.name.slice(0, 80),
      message: value.message.slice(0, MAX_STRING_LENGTH),
    };
  }
  if (Array.isArray(value)) {
    return value.slice(0, MAX_ARRAY_LENGTH).map((entry) => sanitiseDiagnosticValue(entry, depth + 1));
  }
  if (typeof value !== 'object') return String(value).slice(0, MAX_STRING_LENGTH);
  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value).slice(0, MAX_OBJECT_KEYS)) {
    result[key] = SECRET_KEY.test(key)
      ? '[redacted]'
      : sanitiseDiagnosticValue(entry, depth + 1);
  }
  return result;
}
